/**
 * AiImportScreen — scan a supplier bill / PO photo and import products via AI.
 *
 *   Upload image → Extract (backend AI) → review & edit grid (with dedup) → Import.
 *
 * Accepts raster images (JPG/PNG/WebP, ≤5 MB); the backend consumes AI quota.
 */

import { useErrorPopup } from '@/hooks/useErrorPopup';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Q } from '@nozbe/watermelondb';
import { ArrowLeft, Upload, Sparkles, Loader2, Check, X, AlertTriangle, RotateCcw } from 'lucide-react';
import { database } from '@/db';
import { Supplier } from '@/db/models/Inventory';
import { useAppStore } from '@/store/useAppStore';
import { useObservable } from '@/hooks/useObservable';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils/cn';
import {
  parseInvoiceForCatalog,
  dedupeAgainstCatalog,
  commitCatalogImport,
  type ParsedCatalogResult,
  type ImportRow,
  type CommitResult,
} from '@/inventory/aiCatalogImport';

const GST_SLABS = [0, 0.25, 3, 5, 12, 18, 28];

export function AiImportScreen() {
  const navigate = useNavigate();
  const storeId = useAppStore((s) => s.activeStoreId);
  const merchantId = useAppStore((s) => s.currentUser?.merchantId);

  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useErrorPopup(error, () => setError(null));
  const [result, setResult] = useState<ParsedCatalogResult | null>(null);
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [supplierId, setSupplierId] = useState<string>('');
  const [committing, setCommitting] = useState(false);
  const [summary, setSummary] = useState<CommitResult | null>(null);

  const suppliers = useObservable<Supplier[]>(
    () => merchantId
      ? database.get<Supplier>('suppliers').query(Q.where('merchant_id', merchantId), Q.where('is_deleted', false)).observe()
      : null,
    [merchantId],
    [],
  );

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const lowFlags = useMemo(() => new Set(result?.low_confidence_fields ?? []), [result]);
  const selectedCount = rows.filter((r) => r.include).length;

  function chooseFile(f: File | null) {
    if (!f) return;
    setError(null);
    setResult(null);
    setRows([]);
    setSummary(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  }

  async function handleExtract() {
    if (!file || !storeId) return;
    setParsing(true);
    setError(null);
    try {
      const res = await parseInvoiceForCatalog(file);
      const matches = await dedupeAgainstCatalog(res.line_items, storeId);
      setResult(res);
      setRows(res.line_items.map((it, i) => ({
        include: true,
        name: it.name ?? '',
        categoryName: it.category_suggestion ?? null,
        hsnCode: it.hsn_code ?? null,
        gstRate: it.gst_rate ?? 0,
        quantity: it.quantity ?? 0,
        purchasePrice: it.purchase_price ?? 0,
        sellingPrice: it.selling_price ?? 0,
        mrp: it.mrp ?? 0,
        barcode: it.barcode ?? null,
        unit: it.unit ?? 'PCS',
        match: matches[i],
      })));
      // Pre-select a supplier if the bill names one we already have.
      if (res.supplier_name) {
        const hit = suppliers.find((s) => s.name.trim().toLowerCase() === res.supplier_name!.trim().toLowerCase());
        if (hit) setSupplierId(hit.id);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Extraction failed.');
    } finally {
      setParsing(false);
    }
  }

  function updateRow(idx: number, patch: Partial<ImportRow>) {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  async function handleImport() {
    if (!storeId || !merchantId || selectedCount === 0) return;
    setCommitting(true);
    setError(null);
    try {
      const res = await commitCatalogImport(rows, {
        storeId,
        merchantId,
        supplierId: supplierId || null,
        reference: result?.invoice_number ?? null,
      });
      setSummary(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Import failed.');
    } finally {
      setCommitting(false);
    }
  }

  // ── Summary state ──────────────────────────────────────────
  if (summary) {
    return (
      <div className="mx-auto max-w-lg space-y-5 pt-10 text-center">
        <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-success/10">
          <Check size={28} className="text-success-ink" />
        </div>
        <div>
          <h1 className="text-heading font-semibold text-fg-primary">Import complete</h1>
          <p className="mt-1 text-body text-fg-secondary">
            {summary.created} added · {summary.updated} updated · {summary.skipped} skipped
          </p>
        </div>
        {summary.errors.length > 0 && (
          <div className="rounded-md border border-warning/20 bg-warning/10 px-4 py-3 text-left text-body text-warning-ink">
            <div className="mb-1 font-medium">{summary.errors.length} row(s) failed:</div>
            <ul className="list-disc pl-5 text-label">
              {summary.errors.map((e, i) => <li key={i}>{e.name}: {e.message}</li>)}
            </ul>
          </div>
        )}
        <div className="flex justify-center gap-2">
          <Button variant="secondary" onClick={() => { setFile(null); setResult(null); setRows([]); setSummary(null); }}>
            Import another
          </Button>
          <Button onClick={() => navigate('/inventory')}>Go to Inventory</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/inventory')} className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-body text-fg-secondary hover:bg-bg-subtle hover:text-fg-primary transition-colors">
          <ArrowLeft size={14} /> Inventory
        </button>
        <div className="flex items-center gap-2">
          <Sparkles size={18} className="text-accent-ink" />
          <h1 className="text-heading font-semibold text-fg-primary">AI import from bill</h1>
        </div>
      </div>

      {rows.length === 0 ? (
        // ── Upload step ──
        <div className="mx-auto flex max-w-xl flex-col items-center gap-4 pt-6 text-center">
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => chooseFile(e.target.files?.[0] ?? null)}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="flex w-full flex-col items-center gap-3 rounded-lg border border-dashed border-stroke-hairline/30 bg-bg-surface px-6 py-10 hover:border-accent/40 transition-colors"
          >
            {previewUrl ? (
              <img src={previewUrl} alt="Bill preview" className="max-h-64 rounded-md object-contain" />
            ) : (
              <>
                <Upload size={28} className="text-fg-tertiary" />
                <div className="text-body text-fg-secondary">Click to choose a photo of the supplier bill or PO</div>
                <div className="text-label uppercase text-fg-tertiary">JPG / PNG / WebP · up to 5 MB</div>
              </>
            )}
          </button>
          {file && <div className="text-label text-fg-tertiary">{file.name}</div>}
          <div className="flex gap-2">
            {file && <Button variant="secondary" onClick={() => fileRef.current?.click()}>Choose different</Button>}
            <Button onClick={handleExtract} disabled={!file || parsing}>
              {parsing ? <><Loader2 size={14} className="animate-spin" /> Reading bill…</> : <><Sparkles size={14} /> Extract products</>}
            </Button>
          </div>
          <p className="max-w-md text-label text-fg-tertiary">
            The AI reads item names, quantities, prices, HSN and GST. You review and edit everything before anything is saved. Existing products are detected to avoid duplicates.
          </p>
        </div>
      ) : (
        // ── Review step ──
        <>
          <div className="flex flex-wrap items-center gap-3">
            <div className="text-body text-fg-secondary">
              {result?.supplier_name && <span className="text-fg-primary">{result.supplier_name}</span>}
              {result?.invoice_number && <span className="text-fg-tertiary"> · {result.invoice_number}</span>}
              <span className="text-fg-tertiary"> · {rows.length} items found</span>
            </div>
            <label className="ml-auto flex items-center gap-2 text-body text-fg-secondary">
              Attach to supplier
              <select
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value)}
                className="h-8 rounded-md border border-stroke-hairline/20 bg-bg-surface px-2 text-body text-fg-primary"
              >
                <option value="">None</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </label>
            <Button variant="secondary" size="sm" onClick={() => { setRows([]); setResult(null); }}>
              <RotateCcw size={13} /> Start over
            </Button>
          </div>

          {(lowFlags.has('hsn_code') || lowFlags.has('gst_rate')) && (
            <div className="flex items-center gap-2 rounded-md border border-warning/20 bg-warning/10 px-3 py-2 text-body text-warning-ink">
              <AlertTriangle size={14} /> Double-check the highlighted HSN / GST values — the AI was unsure.
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-stroke-hairline/10 bg-bg-surface">
            <table className="w-full text-body">
              <thead className="sticky top-0 bg-bg-surface text-label uppercase text-fg-tertiary">
                <tr className="border-b border-stroke-hairline/10">
                  <th className="px-2 py-2 text-center font-normal">
                    <input
                      type="checkbox"
                      checked={rows.every((r) => r.include)}
                      onChange={(e) => setRows((prev) => prev.map((r) => ({ ...r, include: e.target.checked })))}
                      aria-label="Toggle all"
                    />
                  </th>
                  <th className="px-2 py-2 text-left font-normal">Product</th>
                  <th className="px-2 py-2 text-left font-normal">Category</th>
                  <th className={cn('px-2 py-2 text-left font-normal', lowFlags.has('hsn_code') && 'text-warning-ink')}>HSN</th>
                  <th className={cn('px-2 py-2 text-left font-normal', lowFlags.has('gst_rate') && 'text-warning-ink')}>GST</th>
                  <th className="px-2 py-2 text-right font-normal">Qty</th>
                  <th className="px-2 py-2 text-right font-normal">Cost</th>
                  <th className="px-2 py-2 text-right font-normal">Sell</th>
                  <th className="px-2 py-2 text-left font-normal">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} className={cn('border-b border-stroke-hairline/10', !row.include && 'opacity-40')}>
                    <td className="px-2 py-1.5 text-center">
                      <input type="checkbox" checked={row.include} onChange={(e) => updateRow(i, { include: e.target.checked })} aria-label={`Include ${row.name}`} />
                    </td>
                    <td className="px-2 py-1.5 min-w-[180px]">
                      <Input value={row.name} onChange={(e) => updateRow(i, { name: e.target.value })} className="h-8" />
                    </td>
                    <td className="px-2 py-1.5 min-w-[120px]">
                      <Input value={row.categoryName ?? ''} onChange={(e) => updateRow(i, { categoryName: e.target.value || null })} className="h-8" placeholder="—" />
                    </td>
                    <td className="px-2 py-1.5 w-[90px]">
                      <Input value={row.hsnCode ?? ''} onChange={(e) => updateRow(i, { hsnCode: e.target.value || null })} className={cn('h-8', lowFlags.has('hsn_code') && 'border-warning/40')} inputMode="numeric" />
                    </td>
                    <td className="px-2 py-1.5 w-[74px]">
                      <select
                        value={row.gstRate}
                        onChange={(e) => updateRow(i, { gstRate: Number(e.target.value) })}
                        className={cn('h-8 w-full rounded-md border bg-bg-surface px-1 text-body text-fg-primary', lowFlags.has('gst_rate') ? 'border-warning/40' : 'border-stroke-hairline/20')}
                      >
                        {GST_SLABS.map((g) => <option key={g} value={g}>{g}%</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-1.5 w-[64px]">
                      <Input value={String(row.quantity)} onChange={(e) => updateRow(i, { quantity: Number(e.target.value) || 0 })} className="h-8 text-right" inputMode="decimal" />
                    </td>
                    <td className="px-2 py-1.5 w-[74px]">
                      <Input value={String(row.purchasePrice)} onChange={(e) => updateRow(i, { purchasePrice: Number(e.target.value) || 0 })} className="h-8 text-right" inputMode="decimal" />
                    </td>
                    <td className="px-2 py-1.5 w-[74px]">
                      <Input value={String(row.sellingPrice)} onChange={(e) => updateRow(i, { sellingPrice: Number(e.target.value) || 0 })} className="h-8 text-right" inputMode="decimal" />
                    </td>
                    <td className="px-2 py-1.5">
                      {row.match.type === 'existing' ? (
                        <span className="inline-flex items-center rounded bg-warning/10 px-1.5 py-0.5 text-label uppercase text-warning-ink" title={`Matches "${row.match.productName}" — stock will be added`}>
                          Existing
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded bg-success/10 px-1.5 py-0.5 text-label uppercase text-success-ink">New</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>


          <div className="flex items-center justify-between">
            <div className="text-label uppercase text-fg-tertiary">
              {selectedCount} of {rows.length} selected · new products created, existing get stock added
            </div>
            <Button onClick={handleImport} disabled={committing || selectedCount === 0}>
              {committing ? <><Loader2 size={14} className="animate-spin" /> Importing…</> : <><Check size={14} /> Import {selectedCount} product{selectedCount === 1 ? '' : 's'}</>}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
